import { api } from './index';

export interface ServiceFee {
  id: string;
  description: string;
  amount: number;
  billingPeriod: string;
  categoryName: string;
  invoiceType: string;
  startReading?: number;
  endReading?: number;
  usage?: number;
  quantity: number;
}

export interface BillDetails {
  id: string;
  billId: string;
  receiptNumber?: string | null;
  apartmentId: string;
  apartmentCode: string;
  apartmentCustomerCode?: string | null;
  apartmentOwner: string;
  apartmentPhone: string;
  apartmentEmail: string;
  building: {
    id: string;
    name: string;
    address: string;
    managementPhone: string;
    managementEmail: string;
    projectCode?: string | null;
  };
  amount: number;
  paymentMethod: string;
  status: string;
  transactionId?: string;
  description: string;
  createdAt: string;
  paidAt?: string;
  preparedBy?: string | null;
  /** 2 = thông báo phí (`receipt`); 1 = Quản lý thu chi / Hoạt động */
  receiptPrintCopyCount?: number;
  serviceFees: ServiceFee[];
}

export interface Bill {
  id: string;
  billId?: string;
  amount: number;
  amountReceived?: number | null;
  creditApplied?: number | null;
  dueDate: string;
  status: 'PENDING' | 'PAID' | 'OVERDUE' | 'CANCELLED';
  description?: string;
  paymentMethod: 'CASH' | 'BANK_TRANSFER' | 'CREDIT_CARD' | 'DEBIT_CARD' | 'ONLINE_PAYMENT' | 'CHECK' | 'OTHER';
  createdAt: string;
  updatedAt: string;
  apartmentId: string;
  userId: string;
  apartment?: {
    id: string;
    code: string;
    ownerName: string;
    building?: {
      id: string;
      name: string;
      address: string;
      managementPhone: string;
      managementEmail: string;
    };
  };
  user?: {
    id: string;
    name: string;
    email: string;
  };
}

export interface BillQuery {
  apartmentId?: string;
  userId?: string;
  fromDate?: string;
  toDate?: string;
}

export const billsApi = {
  // Get bills with optional filtering
  getBills: async (query?: BillQuery): Promise<Bill[]> => {
    const params = new URLSearchParams();
    if (query?.apartmentId) params.append('apartmentId', query.apartmentId);
    if (query?.userId) params.append('userId', query.userId);
    if (query?.fromDate) params.append('fromDate', query.fromDate);
    if (query?.toDate) params.append('toDate', query.toDate);

    const response = await api.get(`/bills?${params.toString()}`);
    return response.data;
  },

  // Get a specific bill by ID
  getBill: async (id: string): Promise<Bill> => {
    const response = await api.get(`/bills/${id}`);
    return response.data;
  },

  // Get detailed bill information with service fees
  getBillDetails: async (billId: string): Promise<BillDetails> => {
    const response = await api.get(`/bills/${billId}/details`);
    return response.data;
  },

  getReceiptHtmlUrl: (billId: string, autoPrint = false): string => {
    const base = (api.defaults.baseURL || '').replace(/\/$/, '');
    const suffix = autoPrint ? '?print=1' : '';
    return `${base}/bills/${billId}/receipt.html${suffix}`;
  },

  fetchReceiptHtml: async (billId: string): Promise<string> => {
    const response = await api.get(`/bills/${billId}/receipt.html`, {
      responseType: 'text',
      transformResponse: [(data) => data],
    });
    return response.data as string;
  },

  fetchBillInvoiceHtml: async (billId: string): Promise<string> => {
    const response = await api.get(`/bills/${billId}/invoice.html`, {
      responseType: 'text',
      transformResponse: [(data) => data],
    });
    return response.data as string;
  },

  getReceiptPdfUrl: (billId: string): string => {
    const base = (api.defaults.baseURL || '').replace(/\/$/, '');
    return `${base}/bills/${billId}/receipt.pdf`;
  },

  // Get bills by apartment ID
  getBillsByApartment: async (apartmentId: string): Promise<Bill[]> => {
    const response = await api.get(`/bills?apartmentId=${apartmentId}`);
    return response.data;
  },

  // Get bills by user ID
  getBillsByUser: async (userId: string): Promise<Bill[]> => {
    const response = await api.get(`/bills?userId=${userId}`);
    return response.data;
  },
}; 